/**
 * 邀请码页面
 * 用户可以查看自己的邀请码、邀请记录和统计数据
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LayoutWithSidebar } from '@/components/LayoutWithSidebar';
import { Gift, Users, Coins, Copy, Check, Loader2, Calendar, User } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

interface InvitationStats {
  total: number;
  completed: number;
  totalRewardQuota: number;
}

interface InvitationInfo {
  invitationCode: string;
  stats: InvitationStats;
  maxInvitations: number;
  rewardQuota: number;
}

interface InvitationRecord {
  id: string;
  invitee_username: string;
  reward_quota: number;
  status: string;
  created_at: string;
}

interface InvitationListResponse {
  success: boolean;
  data: InvitationRecord[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export function InvitationPage() {
  const [loading, setLoading] = useState(true);
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
  const [invitationList, setInvitationList] = useState<InvitationRecord[]>([]);
  const [copied, setCopied] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // 获取邀请信息
  const loadInvitationInfo = useCallback(async () => {
    try {
      setLoading(true);
      const token = useAuthStore.getState().accessToken;
      const response = await fetch('/api/user/invitation', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setInvitationInfo(data.data);
      }
    } catch (error) {
      console.error('获取邀请信息失败:', error);
      toast.error('获取邀请信息失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取邀请列表
  const loadInvitationList = useCallback(async (page: number) => {
    try {
      setListLoading(true);
      const token = useAuthStore.getState().accessToken;
      const response = await fetch(`/api/user/invitation/list?page=${page}&pageSize=10`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data: InvitationListResponse = await response.json();
      if (data.success) {
        setInvitationList(data.data);
        setTotalPages(data.pagination.totalPages);
        setCurrentPage(data.pagination.page);
      }
    } catch (error) {
      console.error('获取邀请列表失败:', error);
      toast.error('获取邀请列表失败');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvitationInfo();
    loadInvitationList(1);
  }, [loadInvitationInfo, loadInvitationList]);

  // 复制邀请码
  const handleCopyCode = async () => {
    if (!invitationInfo?.invitationCode) return;

    const registerUrl = `${window.location.origin}/register?invitationCode=${invitationInfo.invitationCode}`;

    try {
      await navigator.clipboard.writeText(registerUrl);
      setCopied(true);
      toast.success('邀请链接已复制到剪贴板');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      // 降级方案：使用传统方法
      const textArea = document.createElement('textarea');
      textArea.value = registerUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        toast.success('邀请链接已复制到剪贴板');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('复制失败，请手动复制邀请码');
      }
      document.body.removeChild(textArea);
    }
  };

  // 复制邀请码（仅代码）
  const handleCopyCodeOnly = async () => {
    if (!invitationInfo?.invitationCode) return;

    try {
      await navigator.clipboard.writeText(invitationInfo.invitationCode);
      toast.success('邀请码已复制到剪贴板');
    } catch (error) {
      toast.error('复制失败，请手动复制');
    }
  };

  // 格式化日期
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 格式化 token 数量
  const formatQuota = (quota: number) => {
    if (quota >= 10000) {
      return `${(quota / 10000).toFixed(1)}万`;
    }
    return quota.toString();
  };

  if (loading) {
    return (
      <LayoutWithSidebar title="邀请好友">
        <div className="flex flex-1 flex-col gap-4 p-4 pt-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </LayoutWithSidebar>
    );
  }

  return (
    <LayoutWithSidebar title="邀请好友">
      <div className="flex flex-1 flex-col gap-4 p-4 pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">邀请好友</h1>
              <p className="text-muted-foreground mt-1">
                邀请好友注册，双方各获得 {formatQuota(invitationInfo?.rewardQuota || 200000)} token 奖励
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {/* 统计卡片 */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">成功邀请</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{invitationInfo?.stats.total || 0}</div>
                <p className="text-xs text-muted-foreground">
                  已邀请 {invitationInfo?.stats.completed || 0} 人完成注册
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">累计奖励</CardTitle>
                <Coins className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatQuota(invitationInfo?.stats.totalRewardQuota || 0)}
                </div>
                <p className="text-xs text-muted-foreground">token</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">邀请额度</CardTitle>
                <Gift className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {invitationInfo?.stats.total || 0} / {invitationInfo?.maxInvitations || 20}
                </div>
                <p className="text-xs text-muted-foreground">
                  还可邀请 {(invitationInfo?.maxInvitations || 20) - (invitationInfo?.stats.total || 0)} 人
                </p>
              </CardContent>
            </Card>
          </div>

          {/* 邀请码卡片 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-orange-500" />
                我的邀请码
              </CardTitle>
              <CardDescription>
                分享邀请链接给好友，注册后双方均可获得奖励
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="text-sm text-muted-foreground mb-1">邀请码</div>
                  <div className="flex items-center gap-2">
                    <div className="font-mono text-xl font-bold bg-muted px-4 py-2 rounded-md">
                      {invitationInfo?.invitationCode || '加载中...'}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleCopyCodeOnly}
                      title="复制邀请码"
                    >
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="text-sm text-muted-foreground mb-1">邀请链接</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-muted px-4 py-2 rounded-md text-sm truncate">
                      {invitationInfo?.invitationCode
                        ? `${window.location.origin}/register?invitationCode=${invitationInfo.invitationCode}`
                        : '加载中...'}
                    </div>
                    <Button onClick={handleCopyCode} className="whitespace-nowrap">
                      {copied ? (
                        <>
                          <Check className="mr-2 h-4 w-4" />
                          已复制
                        </>
                      ) : (
                        <>
                          <Copy className="mr-2 h-4 w-4" />
                          复制链接
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <Alert>
                <Gift className="h-4 w-4" />
                <AlertDescription>
                  好友使用您的邀请码注册后，双方各获得 <strong>{formatQuota(invitationInfo?.rewardQuota || 200000)} token</strong> 奖励！
                  每个用户最多可邀请 <strong>{invitationInfo?.maxInvitations || 20}</strong> 人。
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* 邀请记录 */}
          <Card>
            <CardHeader>
              <CardTitle>邀请记录</CardTitle>
              <CardDescription>查看您邀请的好友列表</CardDescription>
            </CardHeader>
            <CardContent>
              {listLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : invitationList.length > 0 ? (
                <div className="space-y-4">
                  {invitationList.map((record) => (
                    <div
                      key={record.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{record.invitee_username}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            {formatDate(record.created_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={record.status === 'completed' ? 'default' : 'secondary'}
                        >
                          {record.status === 'completed' ? '已完成' : '处理中'}
                        </Badge>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Coins className="h-3 w-3" />
                          +{formatQuota(record.reward_quota)}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* 分页 */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === 1}
                        onClick={() => loadInvitationList(currentPage - 1)}
                      >
                        上一页
                      </Button>
                      <span className="text-sm text-muted-foreground">
                        第 {currentPage} / {totalPages} 页
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={currentPage === totalPages}
                        onClick={() => loadInvitationList(currentPage + 1)}
                      >
                        下一页
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">暂无邀请记录</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    分享您的邀请码给好友，开始邀请吧！
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
      </div>
    </LayoutWithSidebar>
  );
}
